import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Button, Box, Paper, Typography, IconButton, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, useMediaQuery, useTheme, Stack, Avatar, CircularProgress, Alert
} from '@mui/material';
import { Store, Edit, DeleteOutline, Search, Restore, People } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, SUCURSALES_LISTAR, SUCURSAL_ELIMINAR, SUCURSAL_REACTIVAR, SUCURSAL_USUARIOS } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const activa = (s) => Number(s.activo) === 1;

function SucursalesList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('activas');
  const [eliminar, setEliminar] = useState(null);

  const [verUsuariosDe, setVerUsuariosDe] = useState(null);
  const [usuariosSucursal, setUsuariosSucursal] = useState([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [errorUsuarios, setErrorUsuarios] = useState('');

  const fetchSucursales = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + SUCURSALES_LISTAR, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setSucursales(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setSucursales([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSucursales(); }, [fetchSucursales]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return sucursales.filter((s) => {
      const okTexto = !t || String(s.nombre || '').toLowerCase().includes(t) || String(s.direccion || '').toLowerCase().includes(t);
      const okEstado = estado === 'todas' || (estado === 'activas' ? activa(s) : !activa(s));
      return okTexto && okEstado;
    });
  }, [sucursales, q, estado]);

  const cambiar = (url, id) => axios.post(API_BASE_URL + url, { id }, { headers: authHeader() })
    .then(() => { setEliminar(null); fetchSucursales(); }).catch(() => setEliminar(null));
  const reactivar = (s) => cambiar(SUCURSAL_REACTIVAR, s.id);

  const abrirUsuarios = (s) => {
    setVerUsuariosDe(s);
    setErrorUsuarios('');
    setUsuariosSucursal([]);
    setCargandoUsuarios(true);
    axios.get(API_BASE_URL + SUCURSAL_USUARIOS, { params: { id: s.id }, headers: authHeader() })
      .then((res) => setUsuariosSucursal(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((e) => setErrorUsuarios(e.response?.data?.message || 'Error al cargar los usuarios.'))
      .finally(() => setCargandoUsuarios(false));
  };

  const columns = [
    {
      field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 160,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{p.row.nombre}</span>
          {!activa(p.row) && <Chip size="small" label="Baja" sx={{ height: 20 }} />}
        </Box>
      ),
    },
    { field: 'direccion', headerName: 'Dirección', flex: 1, minWidth: 160 },
    { field: 'wap', headerName: 'WhatsApp', width: 130 },
    {
      field: 'usuarios', headerName: 'Usuarios', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Chip
          size="small" icon={<People sx={{ fontSize: 15 }} />} label={p.row.usuarios} variant="outlined"
          clickable onClick={(e) => { e.stopPropagation(); abrirUsuarios(p.row); }}
        />
      ),
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false, filterable: false, align: 'right',
      renderCell: (p) => activa(p.row) ? (
        <>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/sucursal?id=${p.row.id}`); }}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(p.row); }}><DeleteOutline fontSize="small" /></IconButton>
        </>
      ) : (
        <IconButton size="small" color="success" title="Reactivar" onClick={(e) => { e.stopPropagation(); reactivar(p.row); }}><Restore fontSize="small" /></IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Store}
          titulo="Sucursales"
          subtitulo={`${filtered.length} de ${sucursales.length} sucursal${sucursales.length !== 1 ? 'es' : ''}`}
          ayuda="Cada usuario (salvo Administrador) ve y puede operar solo en las sucursales que tenga asignadas acá."
          accion={<Button variant="contained" color="primary" onClick={() => navigate('/sucursal?mode=create')}>Nueva sucursal</Button>}
        />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Buscar por nombre o dirección…"
            value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: '1 1 240px', maxWidth: 360 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <MenuItem value="activas">Activas</MenuItem>
              <MenuItem value="inactivas">Dadas de baja</MenuItem>
              <MenuItem value="todas">Todas</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1}>
            {filtered.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>}
            {filtered.map((s) => {
              const act = activa(s);
              return (
                <Paper key={s.id} variant="outlined" onClick={() => act && navigate(`/sucursal?id=${s.id}`)}
                  sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: act ? 'pointer' : 'default', opacity: act ? 1 : 0.6 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.nombre}</Typography>
                      {!act && <Chip size="small" label="Baja" sx={{ height: 18 }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.direccion || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.wap ? `WhatsApp: ${s.wap} · ` : ''}
                      <Box component="span" onClick={(e) => { e.stopPropagation(); abrirUsuarios(s); }}
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                        {s.usuarios} usuario(s)
                      </Box>
                    </Typography>
                  </Box>
                  {act ? (
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(s); }}><DeleteOutline fontSize="small" /></IconButton>
                  ) : (
                    <Button size="small" color="success" startIcon={<Restore />} onClick={(e) => { e.stopPropagation(); reactivar(s); }}>Reactivar</Button>
                  )}
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <div style={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={filtered} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
              getRowId={(r) => r.id} disableSelectionOnClick rowHeight={48}
              getRowClassName={(params) => activa(params.row) ? '' : 'row-baja'}
              onRowClick={(params) => { if (activa(params.row)) navigate(`/sucursal?id=${params.row.id}`); }}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .row-baja': { opacity: 0.55, cursor: 'default' } }}
            />
          </div>
        )}
      </Box>

      <Dialog open={!!eliminar} onClose={() => setEliminar(null)}>
        <DialogTitle>Dar de baja sucursal</DialogTitle>
        <DialogContent>
          <Typography variant="body2">¿Dar de baja <strong>{eliminar?.nombre}</strong>? Podés reactivarla después desde el filtro "Dadas de baja".</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminar(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => cambiar(SUCURSAL_ELIMINAR, eliminar.id)}>Dar de baja</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!verUsuariosDe} onClose={() => setVerUsuariosDe(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Usuarios en {verUsuariosDe?.nombre}</DialogTitle>
        <DialogContent>
          {cargandoUsuarios ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : errorUsuarios ? (
            <Alert severity="error">{errorUsuarios}</Alert>
          ) : usuariosSucursal.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No hay usuarios asignados a esta sucursal.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {usuariosSucursal.map((u) => (
                <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem' }}>{(u.username || '?').charAt(0).toUpperCase()}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.username}</Typography>
                  </Box>
                  <Chip size="small" label={u.perfil_nombre || '—'} variant="outlined" />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {verUsuariosDe && (
            <Button onClick={() => navigate(`/sucursal?id=${verUsuariosDe.id}`)}>Editar sucursal</Button>
          )}
          <Button onClick={() => setVerUsuariosDe(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SucursalesList;
